/* eslint-disable no-console */
import { useCallback, useEffect, useMemo } from 'react';

import { useCacheQuery } from '../../../hooks/use-cache-query';
import {
  BaseHealthProfessionalSignalRMessage,
  BaseSignalRMessage,
  SignalRAppointmentMessage,
  SignalRFilterKey,
  SignalRTemplateMessage,
} from '../../../hooks/use-signalr';
import { useAppContext } from '../../context/app-context';
import {
  AgendaBlockItemDataFragment,
  AgendaItemDataFragment,
  GetAgendaItemsDocument,
  GetAgendaItemsQueryVariables,
  PushType,
} from '../../types/api/graphql';
import { removeTimezone } from '../../utils/date.utils';

export function useAgendaItem(variables: GetAgendaItemsQueryVariables) {
  const {
    data: agendaItemsData,
    loading: agendaItemsLoading,

    refetch,
  } = useCacheQuery(GetAgendaItemsDocument, {
    variables,
    skip:
      !variables.healthProviderId ||
      !variables.healthProfessionalId ||
      !variables.date,
  });

  const { signalRMessage, addFilter } = useAppContext();

  const filter = useCallback(
    (message: BaseSignalRMessage) => {
      if (
        [
          PushType.AppointmentAdded,
          PushType.AppointmentDeleted,
          PushType.AppointmentUpdated,
          PushType.AgendaTemplateApplied,
        ].includes(message.type)
      ) {
        const { healthProviderId, healthProfessionalId } =
          message as BaseHealthProfessionalSignalRMessage;
        let correctDate = false;

        if (message.type === PushType.AgendaTemplateApplied) {
          const template = message as SignalRTemplateMessage;
          const startDate = new Date(template.startDate).setHours(0, 0, 0, 0);
          const endDate = new Date(template.endDate).setHours(0, 0, 0, 0);
          const currentDate = new Date(variables.date).setHours(0, 0, 0, 0);

          correctDate = currentDate >= startDate && currentDate <= endDate;
        } else {
          const agenda = message as SignalRAppointmentMessage;
          const startDate = new Date(agenda.startTime).setHours(0, 0, 0, 0);
          const currentDate = new Date(variables.date as string).setHours(
            0,
            0,
            0,
            0
          );

          correctDate = startDate === currentDate;
        }
        if (
          healthProviderId === variables.healthProviderId &&
          healthProfessionalId === variables.healthProfessionalId &&
          correctDate
        ) {
          return true;
        }
      }
      return false;
    },
    [variables.date, variables.healthProfessionalId, variables.healthProviderId]
  );

  useEffect(() => {
    if (agendaItemsLoading) return;
    if (
      variables.healthProviderId &&
      variables.healthProfessionalId &&
      variables.healthProviderId
    ) {
      addFilter({ key: SignalRFilterKey.AGENDA, filter });
    }
  }, [
    addFilter,
    agendaItemsLoading,
    filter,
    variables.healthProfessionalId,
    variables.healthProviderId,
  ]);

  useEffect(() => {
    if (signalRMessage && filter(signalRMessage)) {
      console.log(`[${SignalRFilterKey.AGENDA}] refetch`);
      refetch();
    }
  }, [filter, refetch, signalRMessage]);

  const data = useMemo(() => {
    if (agendaItemsData?.agenda) {
      return {
        ...agendaItemsData.agenda,
        blocks: agendaItemsData.agenda.blocks.map(
          block =>
            ({
              ...block,
              slots: block.slots.map(slot => ({
                ...slot,
                startTime: removeTimezone(slot.startTime),
                endTime: removeTimezone(slot.endTime),
              })),
            } as AgendaBlockItemDataFragment)
        ),
      } as AgendaItemDataFragment;
    }

    return null;
  }, [agendaItemsData?.agenda]);

  return {
    data,
    loading: agendaItemsLoading,
  };
}
